/**
 * orgChart.ts — pure tree logic for the HR Org Chart (E360-3), kept
 * component-free so vitest pins the rules (the closeFlow/leadsFlow pattern).
 *
 * The forest rules (all pinned decisions):
 *  - FOREST + UNASSIGNED: real trees render as trees; an employee with no
 *    manager AND no reports goes to the flat "No manager recorded" section.
 *    An employee with no manager but WITH reports is a real root. No
 *    fabricated root, ever — the mockup's "Founder" node is demo data.
 *  - LEAVERS: included ONLY while they hold reports (E360-1 pinned that a
 *    departing manager keeps their reports; hiding them would orphan the
 *    subtree). A leaver nobody reports to is excluded — the Field Team /
 *    OwnerSelect precedent.
 *  - GUARDS, non-optional: the DB can hold a cycle (raw SQL, concurrent
 *    writes — the write-time walk in employees/org.go guards app writes
 *    only), so the builder carries a visited set and a depth cap, and a
 *    rootless cycle (A→B→A: neither is ever a root) is BROKEN deterministically
 *    at its lowest id rather than silently vanishing from the page.
 */

export interface OrgEmployee {
  id: string
  fullName: string
  jobTitle?: string
  status: string // 'active' | 'leaver' (derived server-side)
  managerId?: string
}

export interface OrgNode {
  employee: OrgEmployee
  children: OrgNode[]
}

export interface OrgForest {
  roots: OrgNode[]
  /** No manager AND no reports — the flat section, with its count. */
  unassigned: OrgEmployee[]
  /** True when a cycle or the depth cap interfered with the render. The
   *  page says so honestly instead of drawing a quietly wrong tree. */
  guardTripped: boolean
}

/** Deeper than this is corrupt data, not an organisation. The write-side
 *  walk caps at 128 (employees/org.go managerWalkLimit); the render cap is
 *  tighter because a DRAWN tree past ~32 levels is unreadable anyway and
 *  the goal here is only to never hang. */
export const ORG_DEPTH_CAP = 32

// Name then id — fully deterministic, so the pixel baselines cannot shuffle
// on equal names.
const byName = (a: OrgEmployee, b: OrgEmployee) =>
  a.fullName.localeCompare(b.fullName) || a.id.localeCompare(b.id)

export function buildOrgForest(all: OrgEmployee[]): OrgForest {
  // Reports are judged over the FULL input, then leavers nobody reports to
  // are excluded. (A chain of leavers can leave an included leaver whose
  // only report was itself excluded — it renders as a childless grey node,
  // which is the honest reading of that data.)
  const reportCount = new Map<string, number>()
  for (const e of all) {
    if (e.managerId) reportCount.set(e.managerId, (reportCount.get(e.managerId) ?? 0) + 1)
  }
  const included = all.filter(e => e.status !== 'leaver' || (reportCount.get(e.id) ?? 0) > 0)
  const byId = new Map(included.map(e => [e.id, e]))

  const children = new Map<string, OrgEmployee[]>()
  for (const e of included) {
    if (e.managerId && byId.has(e.managerId)) {
      const list = children.get(e.managerId) ?? []
      list.push(e)
      children.set(e.managerId, list)
    }
  }

  // A root: no manager recorded, or a manager the fetched/included set does
  // not contain (an orphan edge still deserves a visible tree, never a
  // silent drop).
  const rootEmployees = included.filter(e => !e.managerId || !byId.has(e.managerId))

  let guardTripped = false
  const visited = new Set<string>()

  function build(e: OrgEmployee, depth: number): OrgNode {
    visited.add(e.id)
    const node: OrgNode = { employee: e, children: [] }
    if (depth >= ORG_DEPTH_CAP) {
      guardTripped = true
      return node
    }
    for (const c of (children.get(e.id) ?? []).slice().sort(byName)) {
      if (visited.has(c.id)) {
        // A back-edge — the cycle guard. Drop the edge, say so.
        guardTripped = true
        continue
      }
      node.children.push(build(c, depth + 1))
    }
    return node
  }

  const roots = rootEmployees.slice().sort(byName).map(e => build(e, 0))

  // A ROOTLESS cycle (A→B→A) reaches no root and would otherwise vanish.
  // Break each such cycle at its lowest id — deterministic, visible, flagged.
  for (const e of included.slice().sort((a, b) => a.id.localeCompare(b.id))) {
    if (!visited.has(e.id)) {
      guardTripped = true
      roots.push(build(e, 0))
    }
  }

  const unassigned = roots
    .filter(r => r.children.length === 0 && !r.employee.managerId)
    .map(r => r.employee)
  const unassignedIds = new Set(unassigned.map(e => e.id))

  return {
    roots: roots.filter(r => !unassignedIds.has(r.employee.id)),
    unassigned,
    guardTripped,
  }
}
